/**
 * Melbourne timezone helpers. Australia/Melbourne is canonical for puzzle
 * selection. The user's device clock is only trusted for the live countdown
 * display, never for game state.
 *
 * Key invariant: nowInMelbourne() reads UTC + tz, never device-local time.
 * DST is handled implicitly by Intl.DateTimeFormat.
 */

export const MELB_TZ = 'Australia/Melbourne';

export interface MelbourneTime {
  ymd: string; // YYYY-MM-DD
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number;
  second: number;
}

const fmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: MELB_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/**
 * Returns Melbourne wall-clock time at the given UTC instant (defaults to now).
 */
export function melbourneAt(utcMs: number = Date.now()): MelbourneTime {
  const parts = fmt.formatToParts(new Date(utcMs));
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  let hour = parseInt(get('hour'), 10);
  // Some Intl impls return "24" for midnight in en-CA + hour12:false. Normalise.
  if (hour === 24) hour = 0;
  const year = parseInt(get('year'), 10);
  const month = parseInt(get('month'), 10);
  const day = parseInt(get('day'), 10);
  const minute = parseInt(get('minute'), 10);
  const second = parseInt(get('second'), 10);
  const ymd = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return { ymd, year, month, day, hour, minute, second };
}

/**
 * Day-of-week for a Melbourne YMD. 0 = Sunday, 6 = Saturday, 1–5 = Mon–Fri.
 */
function dowOf(ymd: string): number {
  const [y, m, d] = ymd.split('-').map((s) => parseInt(s, 10));
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/**
 * The "active" Melbourne calendar date for puzzle selection.
 *
 * Schedule: weekday-only puzzles, Mon–Fri 9am Melbourne rollover.
 *  - Mon hr <9    → last Friday's puzzle
 *  - Mon hr >=9   → today (Monday)
 *  - Tue–Fri hr <9 → yesterday (a weekday)
 *  - Tue–Fri hr >=9 → today
 *  - Sat / Sun     → last Friday's puzzle (extends through weekend)
 */
export function activePuzzleDate(utcMs: number = Date.now()): string {
  const m = melbourneAt(utcMs);
  let candidate = m.hour < 9 ? shiftYmd(m.ymd, -1) : m.ymd;
  while (true) {
    const dow = dowOf(candidate);
    if (dow >= 1 && dow <= 5) return candidate;
    candidate = shiftYmd(candidate, -1);
  }
}

/** Returns true if Melbourne time is currently before 9am. */
export function isPreNineMelbourne(utcMs: number = Date.now()): boolean {
  return melbourneAt(utcMs).hour < 9;
}

/**
 * Compute the absolute UTC ms for the next Melbourne 09:00:00 cutover that
 * lands on a WEEKDAY (Mon–Fri). On Friday ≥9am, Saturday, Sunday, or Monday
 * <9am the next cutover is Monday 9am.
 */
export function nextNineAmMelbourne(nowUtcMs: number = Date.now()): number {
  const m = melbourneAt(nowUtcMs);
  let targetYmd = m.hour < 9 ? m.ymd : shiftYmd(m.ymd, 1);
  while (true) {
    const dow = dowOf(targetYmd);
    if (dow >= 1 && dow <= 5) break;
    targetYmd = shiftYmd(targetYmd, 1);
  }
  return melbourneWallClockToUtc(targetYmd, 9, 0, 0, nowUtcMs);
}

/** ms remaining until the next Melbourne 09:00 cutover. */
export function msUntilNextNineAm(nowUtcMs: number = Date.now()): number {
  return nextNineAmMelbourne(nowUtcMs) - nowUtcMs;
}

/**
 * Find the UTC ms whose Melbourne wall-clock matches the given (ymd, h, m, s).
 *
 * Implementation: the offset between UTC and Melbourne is at most ±11h. Start
 * from the naive UTC interpretation of the wall clock, compute its Melbourne
 * representation, then iteratively correct (max 3 passes — DST flips can shift
 * the answer by 1h, so 3 iterations is overkill but safe).
 */
export function melbourneWallClockToUtc(
  ymd: string,
  hour: number,
  minute: number,
  second: number,
  searchAnchorMs: number = Date.now()
): number {
  // First guess: treat ymd + hour as if it were UTC, then we'll measure error.
  const [y, mo, d] = ymd.split('-').map((s) => parseInt(s, 10));
  let guess = Date.UTC(y, mo - 1, d, hour, minute, second);

  for (let i = 0; i < 4; i++) {
    const got = melbourneAt(guess);
    const err = msBetweenWallClocks(got, { ymd, hour, minute, second });
    if (err === 0) return guess;
    guess -= err;
  }

  // Final sanity: if we're more than 26h away from the search anchor, walk
  // forward day-by-day until Melbourne wall hits the target.
  if (Math.abs(guess - searchAnchorMs) > 26 * 3600_000) {
    // Anchor failed — fall through, return best guess.
  }
  return guess;
}

/** Difference, in ms, between two wall-clock times (treating them as if equivalent). */
function msBetweenWallClocks(
  got: MelbourneTime,
  want: { ymd: string; hour: number; minute: number; second: number }
): number {
  const [gy, gmo, gd] = got.ymd.split('-').map((s) => parseInt(s, 10));
  const [wy, wmo, wd] = want.ymd.split('-').map((s) => parseInt(s, 10));
  const gMs = Date.UTC(gy, gmo - 1, gd, got.hour, got.minute, got.second);
  const wMs = Date.UTC(wy, wmo - 1, wd, want.hour, want.minute, want.second);
  return gMs - wMs;
}

/** Shift a YMD by N days. Handles month/year boundaries via Date math (UTC anchored). */
export function shiftYmd(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.split('-').map((s) => parseInt(s, 10));
  const utc = Date.UTC(y, m - 1, d);
  const shifted = new Date(utc + deltaDays * 86_400_000);
  const ny = shifted.getUTCFullYear();
  const nm = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const nd = String(shifted.getUTCDate()).padStart(2, '0');
  return `${ny}-${nm}-${nd}`;
}

/** Returns the day-name (e.g. 'Tuesday') for the given Melbourne YMD. */
export function dayName(ymd: string): string {
  const [y, m, d] = ymd.split('-').map((s) => parseInt(s, 10));
  // Use UTC noon to avoid DST edge cases.
  const noon = new Date(Date.UTC(y, m - 1, d, 12));
  return noon.toLocaleDateString('en-AU', { weekday: 'long', timeZone: 'UTC' });
}

/** Format a duration in ms to the countdown style "14h 22m" or "59s". */
export function formatCountdown(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (hours > 0) return `${hours}h ${String(mins).padStart(2, '0')}m`;
  if (mins > 0) return `${mins}m ${String(secs).padStart(2, '0')}s`;
  return `${secs}s`;
}
